/**
 * The import path's own rules: what a document may be, and how big.
 *
 * A design exported from Canva, Figma or PowerPoint arrives as a PDF, and the
 * server rasterises page 1 of it. The image path is the ordinary upload, and it
 * keeps `lib/storage`'s rules — this module only covers the document.
 */

/** Client-side ceiling on an imported document. The API enforces its own. */
export const MAX_PDF_BYTES = 20 * 1024 * 1024;

/** Value for an `<input type="file" accept="...">` attribute. */
export const PDF_ACCEPT_ATTRIBUTE = 'application/pdf,.pdf';

function formatMegabytes(bytes: number): string {
  return `${Math.round((bytes / (1024 * 1024)) * 10) / 10} MB`;
}

/**
 * Returns a rejection message, or null when the document is acceptable.
 *
 * The extension is accepted alongside the MIME type because some browsers send
 * an empty `type` for a file dragged out of a cloud drive; the server reads the
 * bytes either way and answers `TEMPLATE_IMPORT_INVALID` if they are not a PDF.
 */
export function validatePdfFile(file: File): string | null {
  const looksLikePdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
  if (!looksLikePdf) {
    return 'That file is not a PDF. Export your design as a PDF and try again.';
  }
  if (file.size > MAX_PDF_BYTES) {
    return `That file is ${formatMegabytes(file.size)}. Keep imports under ${formatMegabytes(
      MAX_PDF_BYTES,
    )}.`;
  }
  return null;
}
